import React, { useEffect, useMemo, useState } from 'react';
import SingleSelect, { SelectOption } from '../../../components/common/Search/SingleSelect';
import FormInput from '../../../components/common/FormInput';
import CustomButton from '../../../components/common/CustomButton';
import { Label, Tooltip } from 'flowbite-react';
import { useLlmService } from '../../../services';
import { useSelector } from "react-redux";
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';

const LLMSettings = () => {
  type ProviderOption = SelectOption & { isDisabled?: boolean };

  const {
    isLLMSettingsLoading: loading,
    llmSettings: settings
  } = useSelector((state: any) => state.settings);

  const providerOptions: ProviderOption[] = useMemo(() => ([
    // { value: 'openai', label: 'OpenAI', isDisabled: true },
    { value: 'gemini', label: 'Google Gemini' }
  ]), []);

  const [provider, setProvider] = useState<SelectOption | null>({ value: 'gemini', label: 'Google Gemini' });
  const [apiKey, setApiKey] = useState<string>('');
  const [model, setModel] = useState<string>('gemini-3-flash-preview');
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [initialState, setInitialState] = useState<{ provider: string; apiKey: string }>({
    provider: 'gemini',
    apiKey: ''
  });

  const { getLlmSettings, createLlmSettings, updateLlmSettings } = useLlmService();

  const hasChanges = useMemo(() => {
    return (
      (provider?.value || '') !== initialState.provider ||
      apiKey !== initialState.apiKey
    );
  }, [provider, apiKey, initialState]);

  const isSaveDisabled = !provider || !apiKey || !hasChanges;

  useEffect(() => {
    let mounted = true;
    getLlmSettings()
      .catch(() => { })
      .finally(() => {
        if (mounted) setIsInitialized(true);
      });
    return () => { mounted = false; };
  }, [getLlmSettings]);

  useEffect(() => {
    if (!settings) return;
    if (settings.provider) {
      const selectedProvider = providerOptions.find(p => p.value === settings.provider);
      if (selectedProvider) setProvider(selectedProvider);
    }
    if (settings.model) setModel(settings.model);
    if (settings.api_key) setApiKey(settings.api_key);
    setInitialState({
      provider: settings.provider || 'gemini',
      apiKey: settings.api_key || ''
    });
  }, [settings, providerOptions]);

  const handleSave = async () => {
    if (isSaveDisabled) return;
    const payload = {
      provider: provider!.value,
      model: model,
      api_key: apiKey,
    };
    const exists = Boolean(settings);
    const ok = exists ? await updateLlmSettings(payload) : await createLlmSettings(payload);
    if (ok) {
      setInitialState({ provider: payload.provider, apiKey: payload.api_key });
    }
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-light-surfacePrimary dark:bg-tertiary-dark w-full">
        <div className="mx-auto px-8 py-8 ">
          <div className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-2">
            <div className="flex items-center gap-4 mb-4">
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">LLM Settings</h1>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter'] mt-1">Configure your AI provider, model, and API key</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-secondary-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-8 space-y-8">
              <div className="h-10 bg-gray-100 dark:bg-slate-700 rounded animate-pulse" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="provider" className="flex text-gray-900 dark:text-white">Provider</Label>
                  </div>
                  <div className="mt-2">
                    <div className="h-10 bg-gray-100 dark:bg-slate-700 rounded animate-pulse" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="api_key" className="flex text-gray-900 dark:text-white">API Key</Label>
                  <div className="mt-2 h-10 bg-gray-100 dark:bg-slate-700 rounded animate-pulse" />
                </div>
              </div>
            </div>

            <div className="px-8 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-end items-center gap-3">
                <div className="!min-w-[120px] h-9 bg-gray-100 dark:bg-slate-700 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-surfacePrimary dark:bg-tertiary-dark w-full">
      <div className="mx-auto px-8 py-8 ">
        <div className="mb-8 border-b border-gray-200 dark:border-gray-700 pb-2">
          <div className="flex items-center gap-4 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white font-['Inter']">
                LLM Settings
              </h1>
              <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 font-['Inter'] mt-1">
                Configure your AI provider, model, and API key
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-secondary-dark rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="p-8 space-y-8">
            {!settings?.api_key ? (
              <div className="px-4 py-3 text-sm rounded-md border border-yellow-300 bg-yellow-50 text-yellow-800 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-200">
                We are currently using our default AI key until you configure your own below. Once you add your API key, usage will be billed to your key.
              </div>
            ) : (
              <div className="px-4 py-3 text-sm rounded-md border border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-200">
                Usage is billed to your configured API key.
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="provider" className="flex text-gray-900 dark:text-white">Provider</Label>
                  <Tooltip
                    style="auto"
                    className="!border-0 !bg-white dark:!bg-slate-800 !text-gray-900 dark:!text-gray-100 shadow-lg"
                    content={
                      <div className="text-xs leading-5">
                        <div>OpenAI will be available soon.</div>
                        <div>We currently support Google Gemini.</div>
                      </div>
                    }
                  >
                    <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-9-4a1 1 0 112 0 1 1 0 01-2 0zM9 9a1 1 0 000 2h1v3a1 1 0 102 0v-4a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                    </svg>
                  </Tooltip>
                </div>
                <div className="mt-2">
                  <SingleSelect
                    key={`provider-${provider?.value || 'none'}`}
                    name="provider"
                    options={providerOptions as unknown as SelectOption[]}
                    placeholder="Select provider"
                    width="w-full"
                    defaultValue={provider || undefined}
                    onChange={(option: any) => {
                      setProvider(option);
                    }}
                  />
                </div>
              </div>

              <div>
                <FormInput
                  id="api_key"
                  name="api_key"
                  label={
                    <span className="flex">
                      API Key
                      <span className="ml-1">*</span>
                    </span>
                  }
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="Enter your provider API key"
                  className='bg-white dark:bg-slate-800'
                />
              </div>
            </div>
          </div>

          <div className="px-8 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex justify-end items-center gap-3">
              <UnsavedChangesIndicator hasUnsavedChanges={hasChanges} />
              <CustomButton
                variant="primary"
                onClick={handleSave}
                disabled={isSaveDisabled || loading}
                className="!min-w-[120px]"
              >
                {loading ? 'Saving...' : 'Save'}
              </CustomButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LLMSettings;


